import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  TrendingUp,
  Building2,
  Users,
  Edit,
  Trash2,
  Plus,
  RefreshCw,
  Activity,
  ArrowUpRight,
  ShieldCheck,
  Clock,
  Zap,
  CreditCard,
  Wallet,
  Sparkles,
  PieChart,
  Layers
} from 'lucide-react';

import { formatDate as formatDateUtil } from '../utils/dateUtils';
import {
  useGetAdminDashboardDetailsQuery,
  useGetAllProjectsQuery,
  useDeleteProjectMutation,
  useGetAllInvestmentsQuery,
  Project,
  Investment
} from '../store/slices/apiSlice';
import { RootState } from '../store';
import { Card } from '../components/ui/Card';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { Table } from '../components/ui/Table';

// Clean markdown artifacts from title strings (e.g. "## Title" or "# **Title**")
const cleanTitle = (text: string | null | undefined): string => {
  if (!text) return 'Untitled Project';
  return text
    .replace(/^#+\s*/g, '')
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/__/g, '')
    .trim();
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const itemsPerPage = 10;

  const { user } = useSelector((state: RootState) => state.auth);

  // Queries & Mutations
  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    refetch: refetchDashboard
  } = useGetAdminDashboardDetailsQuery();

  const {
    data: projectsResponse,
    isLoading: projectsLoading,
    refetch: refetchProjects
  } = useGetAllProjectsQuery({
    page: currentPage,
    limit: itemsPerPage,
  });

  const {
    data: investmentsResponse,
    isLoading: investmentsLoading,
    refetch: refetchInvestments
  } = useGetAllInvestmentsQuery();

  const [deleteProject, { isLoading: isDeletingProject }] = useDeleteProjectMutation();

  const projects: Project[] = projectsResponse?.data || [];
  const investments: Investment[] = investmentsResponse?.data || [];

  // Modal State for Delete Confirmation
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectedProjectTitle, setSelectedProjectTitle] = useState<string>('');

  // Calculate Metrics
  const totalApprovedAmount = useMemo(() => {
    return investments
      .filter((inv) => inv.status === 'approved')
      .reduce((sum, inv) => {
        const val = parseFloat(String(inv.amount_invested || inv.amount || 0));
        return sum + (isNaN(val) ? 0 : val);
      }, 0);
  }, [investments]);

  const pendingInvestmentsCount = useMemo(() => {
    return investments.filter((inv) => inv.status === 'pending').length;
  }, [investments]);

  const categoryBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    projects.forEach((p) => {
      const catName = p.category?.name || 'General';
      counts[catName] = (counts[catName] || 0) + 1;
    });
    const total = projects.length || 1;
    return Object.entries(counts).map(([name, count]) => ({
      name,
      count,
      percentage: Math.round((count / total) * 100)
    }));
  }, [projects]);

  const formatCurrency = (amount: number | string | null | undefined, currencySymbol: string = '₦') => {
    const value = typeof amount === 'number' ? amount : parseFloat(String(amount || '0')) || 0;
    return `${currencySymbol}${new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value)}`;
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([refetchDashboard(), refetchProjects(), refetchInvestments()]);
    setTimeout(() => setIsRefreshing(false), 800);
    toast.success('Dashboard data updated');
  };

  const handleEditProject = (project: Project) => {
    navigate('/admin/update-project', { state: { project } });
  };

  const handleDeleteProject = (projectId: string) => {
    const project = projects.find((p) => p.project_id.toString() === projectId);
    setSelectedProjectId(projectId);
    setSelectedProjectTitle(cleanTitle(project?.title));
    setIsDeleteConfirmationOpen(true);
  };

  const confirmDeleteProject = async () => {
    try {
      const result = await deleteProject({ project_id: parseInt(selectedProjectId, 10) }).unwrap();
      if (result.success) {
        toast.success('Project deleted successfully!');
      } else {
        toast.error(`Failed to delete project: ${result.message}`);
      }
    } catch (error: unknown) {
      const err = error as { data?: { message?: string } };
      toast.error(err?.data?.message || 'Error occurred while deleting project.');
    } finally {
      setIsDeleteConfirmationOpen(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
      case 'funded':
        return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'closed':
        return 'bg-gray-100 text-gray-800 border border-gray-200';
      case 'draft':
        return 'bg-amber-100 text-amber-800 border border-amber-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  // Table Columns Setup
  const tableColumns = [
    {
      key: 'sno',
      label: 'S.No',
      render: (_: unknown, __: unknown, index: number) => (
        <span className="font-bold text-gray-400 text-xs">#{index + 1}</span>
      ),
    },
    {
      key: 'image',
      label: 'Image',
      render: (_: unknown, row: Project) => {
        const hasImage = row.images && row.images.length > 0 && row.images[0].image_url;
        return hasImage ? (
          <img
            src={row.images[0].image_url}
            alt={cleanTitle(row.title)}
            className="w-14 h-11 object-cover rounded-lg shadow-sm border border-gray-100"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="w-14 h-11 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-500 shadow-sm">
            <Building2 className="w-5 h-5" />
          </div>
        );
      },
    },
    {
      key: 'title',
      label: 'Project Title',
      sortable: true,
      render: (value: string) => (
        <div className="min-w-[180px]">
          <p className="font-bold text-gray-900 text-sm line-clamp-1">{cleanTitle(value)}</p>
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      render: (_: unknown, row: Project) => (
        <span className="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-100 capitalize">
          {row.category?.name || 'Real Estate'}
        </span>
      ),
    },
    {
      key: 'location',
      label: 'Location',
      sortable: true,
      render: (value: string) => (
        <span className="text-xs font-medium text-gray-600 truncate max-w-[120px] block">
          {value || 'Nigeria'}
        </span>
      ),
    },
    {
      key: 'funding_goal',
      label: 'Target Goal',
      sortable: true,
      render: (value: number, row: Project) => (
        <span className="font-semibold text-gray-900 text-sm">
          {formatCurrency(value, row.currency_symbol || '₦')}
        </span>
      ),
    },
    {
      key: 'amount_raised',
      label: 'Progress',
      sortable: true,
      render: (value: number | null, row: Project) => {
        const raised = value || 0;
        const goal = row.funding_goal || 1;
        const pct = Math.min(100, Math.round((raised / goal) * 100));
        return (
          <div className="w-28 space-y-1">
            <div className="flex justify-between items-center text-xs">
              <span className="font-bold text-gray-800">{formatCurrency(raised, row.currency_symbol || '₦')}</span>
              <span className="text-[10px] font-extrabold text-blue-600">{pct}%</span>
            </div>
            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  pct >= 100 ? 'bg-emerald-500' : 'bg-blue-600'
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value: string) => (
        <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-full capitalize ${getStatusBadge(value)}`}>
          {value || 'Active'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: unknown, row: Project) => (
        <div className="flex items-center gap-1.5">
          <Button
            onClick={() => handleEditProject(row)}
            variant="outline"
            size="sm"
            className="p-1.5 text-blue-600 hover:bg-blue-50 border-blue-200"
            disabled={isDeletingProject}
            title="Edit Project"
          >
            <Edit className="w-3.5 h-3.5" />
          </Button>
          {(user?.role === 'admin' || user?.role === 'manager') && (
            <Button
              onClick={() => handleDeleteProject(row.project_id.toString())}
              variant="outline"
              size="sm"
              className="p-1.5 text-red-600 hover:bg-red-50 border-red-200"
              disabled={isDeletingProject}
              title="Delete Project"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 pb-8">
      {/* Top Banner & Control Bar */}
      <div className="bg-white border border-gray-200/80 rounded-2xl p-5 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">Platform Overview</h1>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              Live Sync
            </span>
          </div>
          <p className="text-sm text-gray-600">
            Real-time analytics, investment performance & project management
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
          <Button
            onClick={() => navigate('/admin/create-project')}
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-100 text-sm font-semibold rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Project
          </Button>
          <Button
            onClick={handleRefresh}
            disabled={isRefreshing}
            variant="outline"
            className="border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-xl"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-4">
        {dashboardLoading ? (
          Array.from({ length: 3 }).map((_, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/3"></div>
            </div>
          ))
        ) : (
          <>

            {/* Total Users */}
            <Card className="p-6 bg-white border border-gray-100 shadow-md shadow-gray-100/50 hover:border-blue-200 transition-all duration-200 group">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-black uppercase tracking-wider text-gray-400">Total Registered Users</p>
                <div className="p-3 bg-blue-50 rounded-xl group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <p className="text-3xl font-black text-gray-900 mb-1">
                {dashboardData?.data?.total_users || 0}
              </p>
              <div className="flex items-center justify-between text-xs pt-2 border-t border-gray-100">
                <span className="text-emerald-600 font-bold flex items-center gap-1">
                  <ArrowUpRight className="w-3.5 h-3.5" /> +12% this month
                </span>
                <button
                  onClick={() => navigate('/admin/users')}
                  className="text-blue-600 hover:underline font-semibold"
                >
                  Manage →
                </button>
              </div>
            </Card>

            {/* Total Projects */}
            <Card className="p-6 bg-white border border-gray-100 shadow-md shadow-gray-100/50 hover:border-emerald-200 transition-all duration-200 group">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-black uppercase tracking-wider text-gray-400">Total Investment Projects</p>
                <div className="p-3 bg-emerald-50 rounded-xl group-hover:scale-110 transition-transform duration-300">
                  <Building2 className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
              <p className="text-3xl font-black text-gray-900 mb-1">
                {dashboardData?.data?.total_projects || projects.length}
              </p>
              <div className="flex items-center justify-between text-xs pt-2 border-t border-gray-100">
                <span className="text-gray-500 font-medium">
                  {projects.filter((p) => p.status === 'funded').length} Fully Funded
                </span>
                <button
                  onClick={() => navigate('/admin/projects')}
                  className="text-emerald-600 hover:underline font-semibold"
                >
                  View All →
                </button>
              </div>
            </Card>

            {/* Pending Approvals */}
            <Card className="p-6 bg-white border border-gray-100 shadow-md shadow-gray-100/50 hover:border-amber-200 transition-all duration-200 group">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-black uppercase tracking-wider text-amber-600">Pending Approvals</p>
                <div className="p-3 bg-amber-50 rounded-xl group-hover:scale-110 transition-transform duration-300">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              <p className="text-3xl font-black text-gray-900 mb-1">
                {pendingInvestmentsCount}
              </p>
              <div className="flex items-center justify-between text-xs pt-2 border-t border-gray-100">
                <span className="text-amber-700 font-semibold flex items-center gap-1">
                  <Zap className="w-3.5 h-3.5 text-amber-500" /> Action Required
                </span>
                <button
                  onClick={() => navigate('/admin/investments')}
                  className="text-amber-600 hover:underline font-bold"
                >
                  Review ({pendingInvestmentsCount}) →
                </button>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Analytics & Performance Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Category Breakdown Card */}
        <Card className="p-6 bg-white border border-gray-100 shadow-md rounded-2xl lg:col-span-1">
          <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                <PieChart className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-base">Category Distribution</h3>
                <p className="text-xs text-gray-500">Active project breakdown</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {categoryBreakdown.length > 0 ? (
              categoryBreakdown.map((cat) => (
                <div key={cat.name} className="space-y-1.5">
                  <div className="flex justify-between items-center text-xs font-semibold">
                    <span className="text-gray-700 capitalize">{cat.name}</span>
                    <span className="text-gray-500">
                      {cat.count} {cat.count === 1 ? 'project' : 'projects'} ({cat.percentage}%)
                    </span>
                  </div>
                  <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-500"
                      style={{ width: `${Math.max(5, cat.percentage)}%` }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-400 text-center py-4">No categories recorded yet.</p>
            )}
          </div>
        </Card>

        {/* System Activity & Quick Insights */}
        <Card className="p-6 bg-white border border-gray-100 shadow-md rounded-2xl lg:col-span-2">
          <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                <Activity className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-base">Recent Platform Activity</h3>
                <p className="text-xs text-gray-500">Live transaction stream & status logs</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/investments')}
              className="text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
            >
              View Investments
            </Button>
          </div>

          <div className="space-y-3">
            {investments.slice(0, 4).map((inv, idx) => (
              <div
                key={inv.investment_id || idx}
                className="flex items-center justify-between p-3.5 rounded-xl bg-gray-50/70 border border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-blue-100/70 text-blue-700 flex items-center justify-center shrink-0">
                    <TrendingUp className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-900">
                      {inv.user?.name || 'Investor'} invested {formatCurrency(inv.amount_invested || inv.amount, inv.currency_symbol || '₦')}
                    </p>
                    <p className="text-[11px] text-gray-500 truncate max-w-[220px]">
                      {cleanTitle(inv.project?.title)}
                    </p>
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <span
                    className={`inline-flex px-2 py-0.5 text-[10px] font-extrabold rounded-full capitalize ${
                      inv.status === 'approved'
                        ? 'bg-emerald-100 text-emerald-800'
                        : inv.status === 'pending'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {inv.status || 'Pending'}
                  </span>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {formatDateUtil(inv.created_at)}
                  </p>
                </div>
              </div>
            ))}

            {investments.length === 0 && (
              <div className="text-center py-6 text-gray-400 text-xs">
                No recent investments recorded.
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Main Table: Recent Projects */}
      <Card className="p-6 bg-white border border-gray-100 shadow-xl rounded-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Recent Projects</h2>
            <p className="text-xs text-gray-500">Manage, update, or inspect platform listings</p>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-xs"
            onClick={() => navigate('/admin/projects')}
          >
            View All Projects ({projects.length})
          </Button>
        </div>

        {projectsLoading ? (
          <div className="py-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
            <p className="text-xs text-gray-500">Loading active projects...</p>
          </div>
        ) : (
          <Table
            columns={tableColumns}
            data={projects}
            searchable={false}
            onRowClick={undefined}
          />
        )}

        {!projectsLoading && projects.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-bold text-gray-800 mb-1">No Projects Found</h3>
            <p className="text-xs text-gray-500 mb-4">Click below to publish your first investment project.</p>
            <Button
              onClick={() => navigate('/admin/create-project')}
              className="bg-blue-600 text-white text-xs font-bold"
            >
              Create Project
            </Button>
          </div>
        )}
      </Card>

      {/* Quick Navigation Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'User Control', icon: Users, path: '/admin/users', color: 'text-blue-600 bg-blue-50' },
          { label: 'Project Treasury', icon: CreditCard, path: '/admin/project-funds', color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Bank Accounts', icon: Wallet, path: '/admin/bank-accounts', color: 'text-amber-600 bg-amber-50' },
          { label: 'Dealers', icon: ShieldCheck, path: '/admin/dealer-management', color: 'text-purple-600 bg-purple-50' },
          { label: 'Marketplace', icon: Layers, path: '/admin/marketplace-listings', color: 'text-indigo-600 bg-indigo-50' },
          { label: 'Sponsorships', icon: Sparkles, path: '/admin/child-sponsorship', color: 'text-pink-600 bg-pink-50' },
        ].map((item) => (
          <button
            key={item.label}
            onClick={() => navigate(item.path)}
            className="p-3.5 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md hover:border-gray-200 transition-all flex flex-col items-center text-center group"
          >
            <div className={`p-2.5 rounded-xl ${item.color} group-hover:scale-110 transition-transform mb-2`}>
              <item.icon className="w-5 h-5" />
            </div>
            <span className="text-xs font-bold text-gray-800 truncate w-full">{item.label}</span>
          </button>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        title="Delete Project"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete <span className="font-bold text-gray-900">"{selectedProjectTitle}"</span>? This action cannot be undone.
          </p>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={() => setIsDeleteConfirmationOpen(false)}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteProject}
              disabled={isDeletingProject}
              className="text-xs bg-red-600 text-white hover:bg-red-700"
            >
              {isDeletingProject ? 'Deleting...' : 'Delete Project'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Dashboard;